import React from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Draft, DraftRow, IngredientOption } from "./productRecipeEditor";
import RecipeIngredientRow from "./RecipeIngredientRow";
import RecipeFieldError, { RecipeErrors } from "./RecipeFieldError";
import RecipeOutput from "./RecipeOutput";
import RecipeNotes from "./RecipeNotes";
import { primary, secondary } from "./recipeStyles";
interface Props {
  draft: Draft;
  productSellingUnit: number;
  disabled: boolean;
  canManage: boolean;
  dirty: boolean;
  saving: boolean;
  errors: RecipeErrors;
  save: (event: React.FormEvent) => Promise<void>;
  change: (values: Partial<Draft>) => void;
  changeRow: (key: number, values: Partial<DraftRow>) => void;
  search: (input: string) => Promise<IngredientOption[]>;
  addIngredient: () => void;
  removeIngredient: (key: number) => void;
  discard: () => void;
}
export default function RecipeEditor({
  draft,
  productSellingUnit,
  disabled,
  canManage,
  dirty,
  saving,
  errors,
  save,
  change,
  changeRow,
  search,
  addIngredient,
  removeIngredient,
  discard,
}: Props) {
  const errorFor = (field: string) => (
    <RecipeFieldError field={field} errors={errors} />
  );
  return (
    <form
      onSubmit={save}
      noValidate
      className="space-y-5"
      aria-label="Recipe editor"
    >
      <RecipeOutput
        draft={draft}
        productSellingUnit={productSellingUnit}
        disabled={disabled}
        errors={errors}
        change={change}
      />
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Ingredients</h3>
          <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-slate-500">
            {draft.rows.length}
          </span>
        </div>
        {errorFor("items")}
        <div className="rounded-lg border border-slate-200">
          <table
            className="block w-full table-fixed border-collapse sm:table"
            aria-label="Recipe ingredients"
          >
            <colgroup className="hidden sm:table-column-group">
              <col />
              <col className="w-24" />
              <col className="w-32" />
              <col className="w-12" />
            </colgroup>
            <thead className="hidden bg-slate-50 text-left text-xs font-medium text-slate-500 sm:table-header-group">
              <tr>
                <th scope="col" className="rounded-tl-lg px-4 py-2.5">
                  Ingredient
                </th>
                <th scope="col" className="px-2 py-2.5 text-right">
                  Quantity
                </th>
                <th scope="col" className="px-2 py-2.5">
                  Unit
                </th>
                <th scope="col" className="rounded-tr-lg">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="block sm:table-row-group">
              {draft.rows.map((row, index) => (
                <RecipeIngredientRow
                  key={row.key}
                  row={row}
                  index={index}
                  rows={draft.rows}
                  disabled={disabled}
                  canManage={canManage}
                  errors={errors}
                  search={search}
                  changeRow={changeRow}
                  onRemove={removeIngredient}
                />
              ))}
              {!draft.rows.length && (
                <tr className="block sm:table-row">
                  <td
                    colSpan={4}
                    className="block px-4 py-9 text-center sm:table-cell"
                  >
                    <p className="text-sm font-medium text-slate-700">
                      No ingredients yet
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Add the products needed for this output quantity.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {canManage && (
            <div className="border-t border-slate-200 px-3 py-2">
              <button
                type="button"
                className={`${secondary} w-full justify-start text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 sm:w-auto`}
                disabled={disabled || draft.rows.length >= 200}
                onClick={addIngredient}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                Add ingredient
              </button>
            </div>
          )}
        </div>
      </div>
      <RecipeNotes
        notes={draft.notes}
        disabled={disabled}
        errors={errors}
        change={change}
      />
      {canManage && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white/95 py-3 backdrop-blur">
          <span className="hidden text-xs text-slate-500 sm:inline">
            {draft.rows.length}{" "}
            {draft.rows.length === 1 ? "ingredient" : "ingredients"}
          </span>
          <div className="ml-auto flex items-center gap-2">
            {dirty && (
              <button
                type="button"
                disabled={saving}
                onClick={discard}
                className={secondary}
              >
                Discard changes
              </button>
            )}
            <button
              type="submit"
              disabled={disabled || !dirty}
              className={primary}
            >
              {saving ? "Saving…" : "Save recipe"}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
